import PageHeader from '../../../layout/page/PageHeader'
import { api } from '../../../../auth'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useToast } from '../../../common/toast/hooks/useToast'
import Common from '../../../common/Common'
import { Alert, Button, Col, Nav, Row, Tab } from 'react-bootstrap'
import Pillars from './Pillars'
import FormActions from '../../../common/forms/FormActions'
import { Link } from 'react-router-dom'
import { useAppSelector } from '../../../../store/hooks'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

const PageLayout = () => {
    const axiosPrivate = api
    const theme = useAppSelector((state) => state.theme.theme)

    const [sections, setSections] = useState<any>([])
    const [sectionsOrder, setSectionsOrder] = useState({})

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/page-components/sections/layout')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                if (response.data.data.sections) {
                    setSections(response.data.data.sections)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load language')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async () => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const response = await axiosPrivate
                .post(
                    'page-components/sections/layout/update',
                    { sectionsOrder },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: response.data.data.msg,
                })
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any)
        }
    }

    const FormFooter = (
        <Fragment>
            <Button
                variant="success"
                disabled={
                    isSubmitting ||
                    isLoading ||
                    Object.keys(sectionsOrder).length <= 0
                }
                onClick={saveRecord}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            Updating layout...
                        </>
                    ) : (
                        'Update Layout'
                    )}
                </span>
            </Button>
        </Fragment>
    )

    return (
        <>
            <PageHeader
                icon="table-columns"
                subtitle="Arrange page sections into layouts"
                header={`Page Layout`}
            />
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            {Object.keys(sections).length > 0 ? (
                <>
                    <Tab.Container
                        id="page-layout-tabs"
                        defaultActiveKey={Object.keys(sections)[0]}
                    >
                        <Row>
                            <Col lg={4} xl={3}>
                                <Nav variant="pills" className="flex-column">
                                    {Object.keys(sections).map(
                                        (key, index: number) => {
                                            const section = sections[key]
                                            return (
                                                <Nav.Item key={index}>
                                                    <Nav.Link eventKey={key}>
                                                        {' '}
                                                        {section.pages_name}
                                                    </Nav.Link>
                                                </Nav.Item>
                                            )
                                        }
                                    )}
                                </Nav>
                            </Col>
                            <Col lg={8} xl={9}>
                                <Tab.Content>
                                    {Object.keys(sections).map(
                                        (key, index: number) => {
                                            const section = sections[key]
                                            return (
                                                <Tab.Pane
                                                    eventKey={key}
                                                    key={index}
                                                >
                                                    <Pillars
                                                        initPillars={
                                                            section.pillars
                                                        }
                                                        setSectionsOrder={
                                                            setSectionsOrder
                                                        }
                                                    />
                                                </Tab.Pane>
                                            )
                                        }
                                    )}
                                </Tab.Content>
                            </Col>
                        </Row>
                    </Tab.Container>
                    <FormActions formFooter={FormFooter} />
                </>
            ) : (
                <>
                    {!isLoading ? (
                        <div
                            className={`container-fluid bg-${
                                theme === 'dark' ? 'dark' : 'light'
                            } text-${
                                theme === 'dark' ? 'light' : 'dark'
                            } p-5 shadow`}
                        >
                            <div
                                className={`bg-${
                                    theme === 'dark' ? 'dark' : 'light'
                                } p-5`}
                            >
                                <h3 className="display-6 fw-bold">
                                    No{' '}
                                    <span className="text-success">Active</span>{' '}
                                    page sections were found!
                                </h3>
                                <hr />
                                <p>
                                    Go back to Page Sections and set some
                                    sections to{' '}
                                    <span className="text-success">Active</span>{' '}
                                    then try again.
                                </p>
                                <Link
                                    className="btn btn-outline-primary"
                                    to={'/content/page-sections'}
                                >
                                    Page Sections
                                </Link>
                            </div>
                        </div>
                    ) : null}
                </>
            )}
        </>
    )
}

export default PageLayout
